import React from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import "../../../include/mapbox-gl-style-switcher/MapBoxGlStyleSwitcherControl.css";
import API from "@API/index";
import { useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import Utils from '@utils/index';
import MapBoxBoundaries from '@classes/MapBoxBoundaries';
import MapboxSiteMarker from './MapboxSiteMarker';
import { Tooltip, Button, Box } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import TutorialButton from '@components/common/TutorialButton';
import { MapboxStyleSwitcherControl } from '../../../include/mapbox-gl-style-switcher/MapBoxGlStyleSwitcherControl';
import { useDispatch } from 'react-redux';
import { setSelectedSite } from '@store/selectedSites/actions';
import StreamMarker from '@classes/MapBox/StreamMarker';
import MapToolbar from '@components/settings/asset/MapToolbar';

type CompanyMapProps = {
  height: string | number;
  multiplesites: {
    sites: Site[];
  }
  onMarkersUpdated: (markers: StreamMarker[]) => void;
  enableEdit: boolean;
  focusedStream?: string | undefined;
  onMarkerFocus?: (marker: StreamMarker) => void;
}

export interface ICompanyMapHandles {
  addStream: (stream: VideoStream) => void;
  removeMarker: (stream: VideoStream) => void;
}

const CompanyMap: React.ForwardRefRenderFunction<ICompanyMapHandles, CompanyMapProps> = (props, ref) => {
  const { height, multiplesites, onMarkersUpdated, enableEdit, onMarkerFocus, focusedStream } = props;

  const dispatch = useDispatch();

  const streams = useSelector((store: IStoreState) => store.streams.streams);
  const rootFontSize = useSelector((store: IStoreState) => store.appView.rootFontSize);
  const selectedSite = useSelector((store: IStoreState) => store.selectedSites.selectedSite);

  const [mapLoaded, setMapLoaded] = React.useState<boolean>(false);

  const siteCoordinates = React.useRef<Map<string, number[]>>(new Map());
  const mapContainer = React.useRef<HTMLDivElement | null>(null);
  const map = React.useRef<mapboxgl.Map | null>(null);

  const streamsRef = React.useRef<VideoStream[]>(streams);
  const mapLoadedRef = React.useRef<boolean>(mapLoaded);
  const multiplesitesRef = React.useRef<{ sites: Site[] } | undefined>(multiplesites)
  const selectedSiteRef = React.useRef<Site | undefined>(selectedSite);
  const reduxSelectedSiteRef = React.useRef<any>(selectedSite);
  const siteCameraMarkers = React.useRef<StreamMarker[]>([]);
  const onMarkersUpdatedRef = React.useRef<any>(onMarkersUpdated);
  const onMarkerFocusRef = React.useRef<any>(onMarkerFocus);

  React.useEffect(() => {
    siteCameraMarkers.current.forEach((marker) => {
      if (focusedStream === undefined) {
        marker.setColor('#000')
      } else if (focusedStream && marker.getKvsName() === focusedStream) {
        marker.setColor('#0048e3')
      } else {
        marker.setColor('#000')
      }
    })
  }, [focusedStream])
  React.useEffect(() => { onMarkerFocusRef.current = onMarkerFocus }, [onMarkerFocus])
  React.useEffect(() => { onMarkersUpdatedRef.current = onMarkersUpdated }, [onMarkersUpdated])
  React.useEffect(() => {
    if (enableEdit === false) {
      siteCameraMarkers.current.forEach((marker) => {
        marker.setInteractable(false);
        marker.removeFov();
      })
    } else {
      siteCameraMarkers.current.forEach((marker) => {
        const t = streams.find(el => el.kvsName === marker.getKvsName());
        marker.setInteractable(true);
        marker.withFov(t?.range || 100, t?.angleEastOfNorth || 0, t?.fovWidth || 70)
      })
    }
  }, [enableEdit, streams])

  React.useImperativeHandle(ref, () => ({
    addStream,
    removeMarker
  }))

  const siteStreams = React.useMemo(() => {
    if (selectedSite) {
      return streams.filter(stream => {
        if (stream.siteId === selectedSite.siteId) {
          if (stream.latitude && stream.longitude) {
            return true;
          }
        }

        return false;
      });
    }

    return [];
  }, [selectedSite, streams])

  React.useEffect(() => {
    streamsRef.current = streams;
  }, [streams])

  React.useEffect(() => {
    mapLoadedRef.current = mapLoaded
  }, [mapLoaded])

  React.useEffect(() => {
    multiplesitesRef.current = multiplesites;
  }, [multiplesites])

  React.useEffect(() => {
    selectedSiteRef.current = selectedSite;
  }, [selectedSite])

  React.useEffect(placeSiteCameras, [enableEdit, mapLoaded, selectedSite, siteStreams, streams]);
  React.useEffect(removeSiteCameras, [mapLoaded, selectedSite, siteStreams, streams]);

  const zoomInToSiteCameras = React.useCallback(() => {
    if (mapLoadedRef.current === false || multiplesitesRef.current) return;

    if (streamsRef.current.length > 0) {
      map.current?.fitBounds(new MapBoxBoundaries(streamsRef.current).generate(0.002) as any, { speed: 6 });
    }
  }, [])

  const zoomInToSelectedSite = React.useCallback(() => {
    if (mapLoadedRef.current === false) return;

    if (selectedSiteRef.current) {
      const coords = siteCoordinates.current.get(selectedSiteRef.current.siteId);
      if (coords) {
        const siteStreams = [{ longitude: coords[0], latitude: coords[1] } as VideoStream];
        map.current?.fitBounds(new MapBoxBoundaries(siteStreams).generate(0.004) as any, { speed: 6 });
      }
    }
  }, [])

  React.useEffect(() => {
    selectedSiteRef.current = selectedSite;
  }, [selectedSite])


  const zoomOutToUSA = React.useCallback(() => {
    if (mapLoadedRef.current === false) return;

    map.current?.fitBounds([-131.74804688, 52.48278022, -68.46679688, 22.59372606] as any, { speed: 6 });
  }, [])

  React.useEffect(load, [mapContainer, map]);
  React.useEffect(cleanUp, []);
  React.useEffect(zoomInToSiteCameras, [mapLoaded, zoomInToSiteCameras]);
  React.useEffect(() => {
    if (selectedSite !== undefined) {
      zoomInToSelectedSite();
      if (map.current) {
        map.current.scrollZoom.enable();
      }
      setTimeout(() => {
      }, 1500)
    } else {
      zoomOutToUSA();
    }
  }, [selectedSite, zoomInToSiteCameras, zoomInToSelectedSite, zoomOutToUSA])

  function addStream(stream: VideoStream) {
    if (selectedSite && selectedSite.coordinates?.latitude && selectedSite.coordinates?.longitude) {
      const target = siteCameraMarkers.current.find(el => el.getKvsName() === stream.kvsName);
      if (!target) {
        const marker = new StreamMarker()
          .setInteractable(true)
          .setLngLat([+selectedSite.coordinates?.longitude, +selectedSite.coordinates?.latitude])
          .addTo(map.current as any)
          .setDraggable(true)
          .withLabel(map.current as mapboxgl.Map, stream.defaultName || stream.kvsName)
          .withMap(map.current as any)
          .withKvsName(stream.kvsName)
          .withFov(stream.range || 100, stream.angleEastOfNorth || 0, stream.fovWidth || 70)
          .on('dragend', () => {
            onMarkersUpdatedRef.current([...siteCameraMarkers.current]);
          })
        marker.onUpdateFov(() => {
          onMarkersUpdatedRef.current([...siteCameraMarkers.current]);
        })
        marker.onFocus((marker) => {
          if (onMarkerFocusRef.current) onMarkerFocusRef.current(marker)
        })
        siteCameraMarkers.current.push(marker);

        onMarkersUpdatedRef.current(Array.from(siteCameraMarkers.current));
      }
    }

  }

  function removeMarker(stream: VideoStream) {
    siteCameraMarkers.current = siteCameraMarkers.current.filter(el => el.getKvsName() !== stream.kvsName);
  }

  /**
   * Handles loading of MapBox logic, set up events listeners and more.
   */
  function load(): void {
    if (mapContainer.current && !map.current) {
      let boundaries = [-131.74804688, 52.48278022, -68.46679688, 22.59372606] as any;

      if (reduxSelectedSiteRef.current && reduxSelectedSiteRef.current.coordinates) {
        try {
          const siteStreams = [{ longitude: reduxSelectedSiteRef.current.coordinates.longitude, latitude: reduxSelectedSiteRef.current.coordinates.latitude } as VideoStream];
          boundaries = new MapBoxBoundaries(siteStreams).generate(0.004) as any;
        } catch (error) {
          boundaries = [-131.74804688, 52.48278022, -68.46679688, 22.59372606] as any;
        }
      }

      const mapOptions = {
        container: mapContainer.current,
        style: Utils.MapBox.generateStylesUrl('streets'),
        bounds: boundaries
      }

      map.current = API.MapBox.initializeMap(mapOptions);
      map.current.on('load', () => {
        const styleSwitcher = new MapboxStyleSwitcherControl();
        map.current?.addControl(styleSwitcher, "bottom-right");
        map.current?.addControl(new mapboxgl.NavigationControl(), "bottom-right")
        map.current?.resize();
        setMapLoaded(true);
      });
    }
  }

  /**
   * Clean up associated data with the mounted mapp.
   */
  function cleanUp() {
    return () => {
      siteCameraMarkers.current.forEach(e => e.remove());
      siteCameraMarkers.current.forEach(e => e.cleanUp(map.current as any))
      siteCameraMarkers.current = [];
      if (map.current) map.current.remove();
    }
  }

  function placeSiteCameras() {
    if (mapLoaded === false || selectedSite === undefined) return;

    siteStreams.forEach((siteStream) => {
      const target = siteCameraMarkers.current.find(el => el.getKvsName() === siteStream.kvsName);
      if (!target) {
        const marker = new StreamMarker()
          .setLngLat([siteStream.longitude, siteStream.latitude])
          .addTo(map.current as any)
          .setDraggable(true)
          .withLabel(map.current as mapboxgl.Map, siteStream.defaultName || siteStream.kvsName)
          .withMap(map.current as any)
          .withKvsName(siteStream.kvsName)
          .withFov(siteStream.range || 100, siteStream.angleEastOfNorth || 0, siteStream.fovWidth || 70)
          .on('dragend', () => {
            onMarkersUpdatedRef.current([...siteCameraMarkers.current]);
          })
        if (enableEdit) {
          marker.setInteractable(true)
        } else {
          marker.setInteractable(false)
          marker.removeFov();
        }
        marker.onUpdateFov(() => {
          onMarkersUpdatedRef.current([...siteCameraMarkers.current]);
        })
        marker.onFocus((marker) => {
          if (onMarkerFocusRef.current) onMarkerFocusRef.current(marker)
        })

        siteCameraMarkers.current.push(marker);
      }
    })

    onMarkersUpdatedRef.current(siteCameraMarkers.current);
  }

  function removeSiteCameras() {
    if (!selectedSite && map.current) {
      siteCameraMarkers.current.forEach(e => e.remove());
      siteCameraMarkers.current.forEach(e => e.cleanUp(map.current as any))
      siteCameraMarkers.current = [];
      return;
    }
  }

  return (
    <>
      {/* Cameras on the map */}
      <div style={{ height: height || '80vh' }} ref={mapContainer} className="map-container">
        <Box
          zIndex={1000}
          position='absolute'
          display="grid"
          gridTemplateColumns="max-content max-content auto"
          gap="0.5rem"
          marginTop='0.5rem'
          marginLeft='0.5rem'
          marginRight='0.5rem'
          width="100%"
        >
          {Boolean(multiplesites) && Boolean(selectedSite) &&
            <Tooltip title="Go back to site selection">
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={() => dispatch(setSelectedSite(undefined))}
                style={{ width: rootFontSize * 2.5, height: rootFontSize * 2.5, backgroundColor: 'white' }}
              >
                <ArrowBack fontSize="small" />
              </Button>
            </Tooltip>
          }
          <TutorialButton
            tutorialType="overview_site_map"
          />
        </Box>
        {mapLoaded && map.current && multiplesites &&
          multiplesites.sites.map(site => (
            <MapboxSiteMarker
              map={map.current}
              site={site}
              onClick={(site: Site) => dispatch(setSelectedSite(site))}
              onCoordinateAquired={(siteId: string, coordinates: number[]) => siteCoordinates.current.set(siteId, coordinates)}
            />
          ))
        }
        {Boolean(focusedStream) &&
          <MapToolbar
            fovRange={siteCameraMarkers.current.find(el => el.getKvsName() === focusedStream)?.getRange() || 0}
            fovWidth={siteCameraMarkers.current.find(el => el.getKvsName() === focusedStream)?.getFovWidth() || 0}
            angleEastOfNorth={siteCameraMarkers.current.find(el => el.getKvsName() === focusedStream)?.getAngleEastOfNorth() || 0}
            setFovRange={() => { }}
            setFovWidth={() => { }}
            setAngleEastOfNorth={() => { }}
            left="25vw"
            onFovRangeChange={(range) => {
              const t = siteCameraMarkers.current.find(el => el.getKvsName() === focusedStream);
              if (t) {
                t.withFov(range, t.getAngleEastOfNorth(), t.getFovWidth())
                t.updateFovMarker();
                onMarkersUpdatedRef.current([...siteCameraMarkers.current]);
              }
            }}
            onAngleEastOfNorthChange={(angleEastOfNorth) => {
              const t = siteCameraMarkers.current.find(el => el.getKvsName() === focusedStream);
              if (t) {
                t.withFov(t.getRange(), angleEastOfNorth, t.getFovWidth())
                t.updateFovMarker();
                onMarkersUpdatedRef.current([...siteCameraMarkers.current]);
              }
            }}
            onFovWidthChange={(fovWidth) => {
              const t = siteCameraMarkers.current.find(el => el.getKvsName() === focusedStream);
              if (t) {
                t.withFov(t.getRange(), t.getAngleEastOfNorth(), fovWidth)
                t.updateFovMarker()
                onMarkersUpdatedRef.current([...siteCameraMarkers.current]);
              }
            }}
          />
        }
      </div>
    </>
  )
}


export default React.forwardRef(CompanyMap);